import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LangToggle } from "../components/LangToggle";
import { t, tk, useLang } from "../lib/i18n";
import { useMe } from "../hooks/useMe";
import { api } from "../lib/api";
import { trackView } from "../lib/track";
import { ReportProblem } from "../components/ReportProblem";
import { LearnerPicker } from "../components/LearnerPicker";
import { Mascot } from "../ui/Art";
import { Icon } from "../ui/Icon";

const SITE_LINKS: [string, string][] = [
  ["/belajar/tadika", tk("Tadika")],
  ["/belajar/tadika/jawi", tk("Jawi")],
  ["/belajar/tadika/kira", tk("Kira")],
  ["/belajar/tadika/mewarna", tk("Mewarna")],
  ["/belajar/tadika/belon", tk("Belon")],
  ["/belajar/tadika/sains", tk("Sains")],
  ["/belajar/tadika/eja", tk("Eja")],
  ["/belajar/tadika/padan", tk("Padan")],
  ["/belajar/permainan", tk("Permainan")],
  ["/beli", tk("Harga & pembelian")],
  ["/redeem", tk("Redeem")],
  ["/kredit", tk("Kredit")],
  ["/terma", tk("Terma")],
  ["/privasi", tk("Privasi")],
  ["/refund", tk("Refund")],
];

const NAV = [
  { to: "/", label: tk("Main"), icon: "home", tint: "bg-sky-400" },
  { to: "/belajar", label: "JomBelajar", icon: "book", tint: "bg-emerald-400" },
  { to: "/belajar/tadika", label: tk("Tadika"), icon: "cap", tint: "bg-pink-400" },
  { to: "/belajar/rendah/1", label: tk("Tahun 1–6"), icon: "pencil", tint: "bg-amber-400" },
  { to: "/uasa", label: "UASA", icon: "chart", tint: "bg-violet-400" },
  { to: "/belajar/permainan", label: tk("Permainan"), icon: "game", tint: "bg-orange-400" },
  { to: "/anak", label: tk("Anak"), icon: "heart", tint: "bg-fuchsia-400" },
  { to: "/affiliate", label: tk("Affiliate"), icon: "gift", tint: "bg-rose-400" },
  { to: "/login", label: tk("Akaun"), icon: "user", tint: "bg-teal-400" },
];
/** The five entries kept on the phone's bottom bar; the rest open from the menu button. */
const BOTTOM = new Set(["/", "/belajar/tadika", "/belajar/rendah/1", "/uasa", "/akaun"]);

/** Admins see an "Admin" link with the number of payments waiting to be confirmed. */
function useAdminNav() {
  const { me } = useMe();
  const { pathname } = useLocation();
  const [waiting, setWaiting] = useState(0);
  const isAdmin = me?.role === "admin";
  useEffect(() => {
    if (!isAdmin) return;
    const load = () => api<{ stats: { awaiting_verification: number } }>("/api/admin/orders?status=submitted&limit=1")
      .then((r) => setWaiting(r.stats.awaiting_verification)).catch(() => undefined);
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [isAdmin, pathname]);
  return isAdmin ? [{ to: "/admin", label: "Admin", badge: waiting }] : [];
}

/** "Log keluar" is shown only while signed in; it ends the session on the server, then reloads the site. */
function LogoutButton() {
  const { me, reload } = useMe();
  const { pathname } = useLocation();
  const [busy, setBusy] = useState(false);
  useEffect(() => { reload(); }, [pathname]);
  if (!me) return null;
  async function logout() {
    setBusy(true);
    try { await api("/api/auth/logout", { method: "POST" }); } catch { /* the cookie is cleared on the next request anyway */ }
    window.location.assign("/");
  }
  return (
    <button
      type="button"
      onClick={logout}
      disabled={busy}
      title={me.email}
      className="whitespace-nowrap rounded-full bg-white px-4 py-2 text-sm font-bold text-blue-900 hover:bg-sky-50 disabled:opacity-60"
    >
      {t("Log keluar")}
    </button>
  );
}

/** Signed-in without a password yet: invite them once to set one, so the next sign-in needs no email. */
function PasswordNudge() {
  const { me, reload } = useMe();
  const { pathname } = useLocation();
  useEffect(() => { reload(); }, [pathname]);
  if (!me || me.hasPassword || pathname.startsWith("/akaun") || pathname.startsWith("/login") || pathname.startsWith("/beli") || pathname.startsWith("/r/")) return null; // /beli asks for the password itself
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-surface-muted p-3 text-sm text-brand-ink">
      <span>{t("Tetapkan kata laluan supaya lain kali anda boleh log masuk tanpa menunggu emel.")}</span>
      <NavLink to="/akaun" className="font-semibold underline">{t("Tetapkan sekarang")}</NavLink>
    </div>
  );
}

type NavItem = { to: string; label: string; icon: string; tint: string; badge?: number };

export function AppShell() {
  const [open, setOpen] = useState(false);
  const lang = useLang(); // re-render (and remount pages) when the UI language changes
  const { pathname } = useLocation();
  useEffect(() => { trackView(pathname); setOpen(false); }, [pathname]);
  // The learning pages are bright and colourful: the dark theme only applies to the admin pages.
  useEffect(() => { if (!pathname.startsWith("/admin")) document.documentElement.classList.remove("dark"); }, [pathname]);
  const { me: navMe } = useMe();
  const admin = useAdminNav();
  // The last item is the account entry: "Akaun" (/akaun) when signed in, "Log masuk" (/login) otherwise.
  const account: NavItem = navMe ? { to: "/akaun", label: tk("Akaun"), icon: "user", tint: "bg-teal-400" } : { to: "/login", label: tk("Log masuk"), icon: "user", tint: "bg-teal-400" };
  const nav: NavItem[] = [...NAV.slice(0, -1), ...admin.map((a) => ({ ...a, icon: "gear", tint: "bg-slate-400" })), account];
  const active = (to: string) => (to === "/" ? pathname === "/" : to === "/belajar" ? pathname === "/belajar" : to === "/belajar/rendah/1" ? pathname.startsWith("/belajar/rendah") : to === "/belajar/tadika" ? pathname.startsWith("/belajar/tadika") : pathname === to || pathname.startsWith(`${to}/`));
  const label = (n: NavItem) => (
    <>
      {t(n.label)}
      {n.badge ? <span className="ml-1 rounded-full bg-accent px-1.5 text-xs font-bold text-white">{n.badge}</span> : null}
    </>
  );
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-sky-50 to-white md:flex">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col gap-2 overflow-y-auto bg-gradient-to-b from-blue-900 via-blue-800 to-indigo-900 p-4 text-white md:flex" aria-label={t("Utama")}>
        <NavLink to="/" aria-label="JomCerdik" className="mb-3 flex items-center gap-2 rounded-2xl bg-white/10 p-2">
          <Mascot pose="duo-hello" className="h-14 w-14 shrink-0" />
          <span className="text-2xl font-black leading-none tracking-tight">Jom<span className="text-amber-400">Cerdik</span></span>
        </NavLink>
        <nav className="flex flex-col gap-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} className={`flex min-h-12 items-center gap-3 rounded-2xl px-3 text-base font-bold transition ${active(n.to) ? "bg-sky-500 text-white shadow-lg" : "text-blue-100 hover:bg-white/10"}`}>
              <span className={`grid h-9 w-9 place-items-center rounded-xl text-white ${n.tint}`}><Icon name={n.icon} className="h-5 w-5" /></span>
              {label(n)}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center justify-between gap-2 rounded-2xl bg-white/10 p-2">
          <LangToggle />
          <LogoutButton />
        </div>
      </aside>
      <div className="min-w-0 flex-1 pb-24 md:pb-0">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-2 border-b border-sky-200 bg-white/95 px-4 py-2 backdrop-blur md:hidden">
          <NavLink to="/" aria-label="JomCerdik" className="flex items-center gap-2">
            <Mascot pose="boy-wave" className="h-11 w-11" />
            <span className="text-xl font-black tracking-tight text-blue-900">Jom<span className="text-amber-500">Cerdik</span></span>
          </NavLink>
          <div className="flex items-center gap-2">
            <LangToggle />
            <button type="button" className="grid h-11 w-11 place-items-center rounded-full bg-sky-100 text-brand" aria-expanded={open} aria-controls="main-nav" aria-label={t("Menu")} onClick={() => setOpen((v) => !v)}>
              <Icon name="menu" />
            </button>
          </div>
        </header>
        {open && (
          <nav id="main-nav" className="grid grid-cols-2 gap-2 border-b border-sky-200 bg-white px-4 pb-3 pt-2 md:hidden" aria-label={t("Utama (mudah alih)")}>
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className={`flex min-h-12 items-center gap-2 rounded-2xl px-3 font-bold ${active(n.to) ? "bg-brand text-white" : "bg-sky-50 text-slate-800"}`}>
                <span className={`grid h-8 w-8 place-items-center rounded-lg text-white ${n.tint}`}><Icon name={n.icon} className="h-5 w-5" /></span>
                {label(n)}
              </NavLink>
            ))}
            <div className="col-span-2 flex justify-end"><LogoutButton /></div>
          </nav>
        )}
        <main className="mx-auto max-w-6xl px-4 py-5 sm:px-8 sm:py-8">
          <PasswordNudge />
          {pathname.startsWith("/belajar") && !pathname.startsWith("/belajar/permainan") && <LearnerPicker />}
          <div className="original-page"><Outlet key={lang} /></div>
        </main>
        <nav className="flex flex-wrap gap-x-4 gap-y-1 border-t border-sky-200 px-4 py-3 text-sm sm:px-8" aria-label={t("Pautan lain")}>
          {SITE_LINKS.map(([to, title]) => (
            <NavLink key={to} to={to} className="font-semibold text-brand underline-offset-4 hover:underline">
              {t(title)}
            </NavLink>
          ))}
        </nav>
        <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-sky-200 px-4 py-3 text-xs text-muted sm:px-8">
          <span>© JomCerdik · Kroni AI Empire</span>
          <ReportProblem />
        </footer>
      </div>
      <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-sky-200 bg-white/95 px-1 pb-1 pt-1 backdrop-blur md:hidden" aria-label={t("Utama")}>
        {nav.filter((n) => BOTTOM.has(n.to) || (n.to === "/login" && !navMe)).slice(0, 5).map((n) => (
          <NavLink key={n.to} to={n.to} className={`flex min-h-14 flex-col items-center justify-center gap-0.5 rounded-2xl text-[11px] font-extrabold ${active(n.to) ? "bg-sky-100 text-brand" : "text-slate-600"}`}>
            <span className={`grid h-8 w-8 place-items-center rounded-xl text-white ${n.tint}`}><Icon name={n.icon} className="h-5 w-5" /></span>
            <span className="leading-none">{t(n.label)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
